import { BaseLabelJSON } from "../../../constants/exportsCountries"
import { DOMESTIC_NUTRITION_KEYS } from "../../../Utils/constants"

export type USAMandatoryNutrient =
  | typeof USA_NUTRITION_KEYS.ENERGY
  | typeof USA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE
  | typeof USA_NUTRITION_KEYS.TOTAL_SUGARS
  | typeof USA_NUTRITION_KEYS.ADDED_SUGARS
  | typeof USA_NUTRITION_KEYS.DIETARY_FIBRE
  | typeof USA_NUTRITION_KEYS.VITAMIN_D
  | typeof USA_NUTRITION_KEYS.CALCIUM
  | typeof USA_NUTRITION_KEYS.IRON
  | typeof USA_NUTRITION_KEYS.POTASSIUM
  | typeof USA_NUTRITION_KEYS.PROTEIN
  | typeof USA_NUTRITION_KEYS.TOTAL_FAT
  | typeof USA_NUTRITION_KEYS.SATURATED_FAT
  | typeof USA_NUTRITION_KEYS.SODIUM
  | typeof USA_NUTRITION_KEYS.CHOLESTEROL
  | typeof USA_NUTRITION_KEYS.TRANS_FAT

export type USAMandatoryNutrientObject = Record<USAMandatoryNutrient, string>

export interface USALabelJson extends BaseLabelJSON {
  mandatory_nutrient: [USAMandatoryNutrientObject]
}

export const USA_NUTRITION_KEYS = {
  ...DOMESTIC_NUTRITION_KEYS,
  VITAMIN_D: "Vitamin_D",
  CALCIUM: "Calcium",
  IRON: "Iron",
  POTASSIUM: "Potassium",
}

export const MICRO_NUTRIENT_KEYS = [
  USA_NUTRITION_KEYS.VITAMIN_D,
  USA_NUTRITION_KEYS.CALCIUM,
  USA_NUTRITION_KEYS.IRON,
  USA_NUTRITION_KEYS.POTASSIUM,
]

export type USANutrientData = {
  key: string
  per_serve_unit: string
  per_serve: string | number
  daily_value: string | number
}

export type USAMandatoryNutrientData = {
  TOTAL_FAT: USANutrientData
  SATURATED_FAT?: USANutrientData
  TRANS_FAT?: USANutrientData
  CHOLESTEROL: USANutrientData
  SODIUM: USANutrientData
  TOTAL_CARBOHYDRATE: USANutrientData
  DIETARY_FIBER: USANutrientData
  TOTAL_SUGARS: USANutrientData
  ADDED_SUGARS: USANutrientData
  PROTEIN: USANutrientData
  VITAMIN_D: USANutrientData
  CALCIUM: USANutrientData
  IRON: USANutrientData
  POTASSIUM: USANutrientData
}

export type USANutritionTableValue = {
  serving_per_container: number | string
  serving_size: number | string
  serving_size_unit: string
  household_quantity: string
  calories: string | number
  mandatory_nutrient: USAMandatoryNutrientData
}

export const USA_DV_VALUES = {
  "Above 18 years": {
    [USA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy",
      daily_value: null,
      unit: "kcal",
    },
    [USA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total Carbohydrate",
      daily_value: 275.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total sugars",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added sugars",
      daily_value: 50.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein",
      daily_value: 50.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total Fat",
      daily_value: 78.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated fat",
      daily_value: 20.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 2300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol",
      daily_value: 300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans fat",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary Fiber",
      daily_value: 28.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron",
      daily_value: 18.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 1300.0,
      unit: "mg",
    },
    //Folate DFE(mcg) to be added
    [USA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine",
      daily_value: 150.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6",
      daily_value: 1.7,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12",
      daily_value: 2.4,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C",
      daily_value: 90,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 11,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium",
      daily_value: 420,
      unit: "mg",
    },

    [USA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D",
      daily_value: 20,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K1",
      daily_value: 120,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Vitamin B1",
      daily_value: 1.2,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Vitamin B2",
      daily_value: 1.3,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin",
      daily_value: 30.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Vitamin B3",
      daily_value: 16.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid",
      daily_value: 5.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E",
      daily_value: 15.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A",
      daily_value: 300.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium",
      daily_value: 55.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper",
      daily_value: 0.9,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 4700.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Chloride",
      daily_value: 2300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese",
      daily_value: 2.3,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus",
      daily_value: 1250.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids",
      daily_value: null,
      unit: "g",
    },
  },
  "1-3 years": {
    [USA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy",
      daily_value: null,
      unit: "kcal",
    },
    [USA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total Carbohydrate",
      daily_value: 95.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total Sugars",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added Sugars",
      daily_value: 25.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein",
      daily_value: 13.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total Fat",
      daily_value: 39.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated Fat",
      daily_value: 10.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 1500.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol",
      daily_value: 300.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans Fat",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary Fiber",
      daily_value: 14.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron",
      daily_value: 7.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 700.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine",
      daily_value: 90.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6",
      daily_value: 0.5,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12",
      daily_value: 0.9,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C",
      daily_value: 15.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 3.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium",
      daily_value: 80.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D",
      daily_value: 15.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K1",
      daily_value: 30.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Vitamin B1",
      daily_value: 0.5,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Vitamin B2",
      daily_value: 0.5,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin",
      daily_value: 8.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Vitamin B3",
      daily_value: 6.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid",
      daily_value: 2.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E",
      daily_value: 6.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A",
      daily_value: 300.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium",
      daily_value: 20.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper",
      daily_value: 0.3,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 3000.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Chloride",
      daily_value: 1500.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese",
      daily_value: 1.2,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus",
      daily_value: 460.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids",
      daily_value: null,
      unit: "g",
    },
  },
  "4-6 years": {
    [USA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy",
      daily_value: null,
      unit: "kcal",
    },
    [USA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total Carbohydrate",
      daily_value: 275.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total Sugars",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added Sugars",
      daily_value: 50.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein",
      daily_value: 50.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total Fat",
      daily_value: 78.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated Fat",
      daily_value: 20.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 2300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol",
      daily_value: 300.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans Fat",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary Fiber",
      daily_value: 28.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron",
      daily_value: 18.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 1300.0,
      unit: "mg",
    },

    [USA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine",
      daily_value: 150.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6",
      daily_value: 1.7,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12",
      daily_value: 2.4,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C",
      daily_value: 90.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 11.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium",
      daily_value: 420.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D",
      daily_value: 20.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K1",
      daily_value: 120.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Vitamin B1",
      daily_value: 1.2,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Vitamin B2",
      daily_value: 1.3,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin",
      daily_value: 30.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Vitamin B3",
      daily_value: 16.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid",
      daily_value: 5.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E",
      daily_value: 15.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A",
      daily_value: 900.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium",
      daily_value: 55.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper",
      daily_value: 0.9,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 4700.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Chloride",
      daily_value: 2300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese",
      daily_value: 2.3,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus",
      daily_value: 1250.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids",
      daily_value: null,
      unit: "g",
    },
  },
  "7-9 years": {
    [USA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy",
      daily_value: null,
      unit: "kcal",
    },
    [USA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total Carbohydrate",
      daily_value: 275.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total Sugars",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added Sugars",
      daily_value: 50.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein",
      daily_value: 50.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total Fat",
      daily_value: 78.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated Fat",
      daily_value: 20.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 2300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol",
      daily_value: 300.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans Fat",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary Fiber",
      daily_value: 28.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron",
      daily_value: 18.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 1300.0,
      unit: "mg",
    },

    [USA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine",
      daily_value: 150.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6",
      daily_value: 1.7,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12",
      daily_value: 2.4,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C",
      daily_value: 90.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 11.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium",
      daily_value: 420.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D",
      daily_value: 20.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K1",
      daily_value: 120.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Vitamin B1",
      daily_value: 1.2,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Vitamin B2",
      daily_value: 1.3,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin",
      daily_value: 30.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Vitamin B3",
      daily_value: 16.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid",
      daily_value: 5.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E",
      daily_value: 15.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A",
      daily_value: 900.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium",
      daily_value: 55.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper",
      daily_value: 0.9,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 4700.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Chloride",
      daily_value: 2300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese",
      daily_value: 2.3,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus",
      daily_value: 1250.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids",
      daily_value: null,
      unit: "g",
    },
  },
  "10-12 years": {
    [USA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy",
      daily_value: null,
      unit: "kcal",
    },
    [USA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total Carbohydrate",
      daily_value: 275.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total Sugars",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added Sugars",
      daily_value: 50.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein",
      daily_value: 50.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total Fat",
      daily_value: 78.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated Fat",
      daily_value: 20.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 2300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol",
      daily_value: 300.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans Fat",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary Fiber",
      daily_value: 28.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron",
      daily_value: 18.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 1300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine",
      daily_value: 150.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6",
      daily_value: 1.7,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12",
      daily_value: 2.4,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C",
      daily_value: 90.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 11.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium",
      daily_value: 420.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D",
      daily_value: 20.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K1",
      daily_value: 120.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Vitamin B1",
      daily_value: 1.2,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Vitamin B2",
      daily_value: 1.3,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin",
      daily_value: 30.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Vitamin B3",
      daily_value: 16.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid",
      daily_value: 5.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E",
      daily_value: 15.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A",
      daily_value: 900.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium",
      daily_value: 55.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper",
      daily_value: 0.9,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 4700.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Chloride",
      daily_value: 2300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese",
      daily_value: 2.3,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus",
      daily_value: 1250.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids",
      daily_value: null,
      unit: "g",
    },
  },
  "13-15 years": {
    [USA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy",
      daily_value: null,
      unit: "kcal",
    },
    [USA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total Carbohydrate",
      daily_value: 275.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total Sugars",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added Sugars",
      daily_value: 50.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein",
      daily_value: 50.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total Fat",
      daily_value: 78.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated Fat",
      daily_value: 20.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 2300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol",
      daily_value: 300.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans Fat",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary Fiber",
      daily_value: 28.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron",
      daily_value: 18.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 1300.0,
      unit: "mg",
    },

    [USA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine",
      daily_value: 150.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6",
      daily_value: 1.7,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12",
      daily_value: 2.4,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C",
      daily_value: 90.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 11.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium",
      daily_value: 420.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D",
      daily_value: 20.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K1",
      daily_value: 120.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Vitamin B1",
      daily_value: 1.2,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Vitamin B2",
      daily_value: 1.3,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin",
      daily_value: 30.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Vitamin B3",
      daily_value: 16.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid",
      daily_value: 5.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E",
      daily_value: 15.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A",
      daily_value: 900.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium",
      daily_value: 55.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper",
      daily_value: 0.9,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 4700.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Chloride",
      daily_value: 2300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese",
      daily_value: 2.3,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus",
      daily_value: 1250.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids",
      daily_value: null,
      unit: "g",
    },
  },
  "16-18 years": {
    [USA_NUTRITION_KEYS.ENERGY]: {
      nutrient_name: "Energy",
      daily_value: null,
      unit: "kcal",
    },
    [USA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE]: {
      nutrient_name: "Total Carbohydrate",
      daily_value: 275.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_SUGARS]: {
      nutrient_name: "Total Sugars",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ADDED_SUGARS]: {
      nutrient_name: "Added Sugars",
      daily_value: 50.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PROTEIN]: {
      nutrient_name: "Protein",
      daily_value: 50.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TOTAL_FAT]: {
      nutrient_name: "Total Fat",
      daily_value: 78.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SATURATED_FAT]: {
      nutrient_name: "Saturated Fat",
      daily_value: 20.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.SODIUM]: {
      nutrient_name: "Sodium",
      daily_value: 2300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLESTEROL]: {
      nutrient_name: "Cholesterol",
      daily_value: 300.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.TRANS_FAT]: {
      nutrient_name: "Trans Fat",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.DIETARY_FIBRE]: {
      nutrient_name: "Dietary Fiber",
      daily_value: 28.0,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.ORYZANOL]: {
      nutrient_name: "Oryzanol",
      daily_value: null,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.IRON]: {
      nutrient_name: "Iron",
      daily_value: 18.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CALCIUM]: {
      nutrient_name: "Calcium",
      daily_value: 1300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.IODINE]: {
      nutrient_name: "Iodine",
      daily_value: 150.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B6]: {
      nutrient_name: "Vitamin B6",
      daily_value: 1.7,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B12]: {
      nutrient_name: "Vitamin B12",
      daily_value: 2.4,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_C]: {
      nutrient_name: "Vitamin C",
      daily_value: 90.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.ZINC]: {
      nutrient_name: "Zinc",
      daily_value: 11.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MAGNESIUM]: {
      nutrient_name: "Magnesium",
      daily_value: 420.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_D]: {
      nutrient_name: "Vitamin D",
      daily_value: 20.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_K1]: {
      nutrient_name: "Vitamin K1",
      daily_value: 120.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B1]: {
      nutrient_name: "Vitamin B1",
      daily_value: 1.2,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B2]: {
      nutrient_name: "Vitamin B2",
      daily_value: 1.3,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.BIOTIN]: {
      nutrient_name: "Biotin",
      daily_value: 30.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_B3]: {
      nutrient_name: "Vitamin B3",
      daily_value: 16.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PANTOTHENIC_ACID]: {
      nutrient_name: "Pantothenic Acid",
      daily_value: 5.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_E]: {
      nutrient_name: "Vitamin E",
      daily_value: 15.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.VITAMIN_A]: {
      nutrient_name: "Vitamin A",
      daily_value: 900.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.SELENIUM]: {
      nutrient_name: "Selenium",
      daily_value: 55.0,
      unit: "mcg",
    },
    [USA_NUTRITION_KEYS.COPPER]: {
      nutrient_name: "Copper",
      daily_value: 0.9,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.POTASSIUM]: {
      nutrient_name: "Potassium",
      daily_value: 4700.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.CHOLINE]: {
      nutrient_name: "Chloride",
      daily_value: 2300.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MANGANESE]: {
      nutrient_name: "Manganese",
      daily_value: 2.3,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.PHOSPHORUS]: {
      nutrient_name: "Phosphorus",
      daily_value: 1250.0,
      unit: "mg",
    },
    [USA_NUTRITION_KEYS.MUFA]: {
      nutrient_name: "Mufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.PUFA]: {
      nutrient_name: "Pufa",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: {
      nutrient_name: "Omega 3 fatty acids",
      daily_value: null,
      unit: "g",
    },
    [USA_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: {
      nutrient_name: "Omega 6 fatty acids",
      daily_value: null,
      unit: "g",
    },
  },
}
